import { useEffect, useReducer, useRef } from "react";

import config from "../../../config";
import { ensureFreshAccessToken } from "../../../auth/ensureFreshAccessToken";
import { useUserContext } from "../../../context/userContext";

// Backend: poker/tournament_consumer.py's TournamentChannelConsumer, at
// ws/poker/mtt/<tournament_id>/ -- a tournament-wide connection independent of
// any table's game_id, for a registered/active/eliminated entrant who isn't
// (or isn't yet) seated at a table. See backend/poker/docs/consumers.md's
// "Closed: no tournament connection while not seated anywhere".

const INITIAL = {
  wsStatus: "disconnected", // "connecting" | "connected" | "disconnected" | "rejected"
  tournamentStatus: null,
  level: null, // { levelIndex, smallBlind, bigBlind, ante, secondsRemaining } | null
  myStatus: null, // "registered" | "active" | "eliminated" | null (never registered / rejected)
  myFinishPosition: null,
  currentTableId: null,
  gameId: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "WS_STATUS":
      return { ...state, wsStatus: action.wsStatus };
    case "SNAPSHOT":
      return {
        ...state,
        tournamentStatus: action.status,
        level:
          action.levelIndex == null
            ? null
            : {
                levelIndex: action.levelIndex,
                smallBlind: action.smallBlind,
                bigBlind: action.bigBlind,
                ante: action.ante,
                secondsRemaining: action.secondsRemaining,
              },
        myStatus: action.myStatus,
        myFinishPosition: action.myFinishPosition,
        currentTableId: action.currentTableId,
        gameId: action.gameId,
      };
    case "BLIND_LEVEL_CHANGED":
      return {
        ...state,
        level: {
          levelIndex: action.levelIndex,
          smallBlind: action.smallBlind,
          bigBlind: action.bigBlind,
          ante: action.ante,
          secondsRemaining: action.secondsRemaining,
        },
      };
    case "PLAYER_ELIMINATED_SELF":
      return { ...state, myStatus: "eliminated", myFinishPosition: action.finishPosition };
    case "TOURNAMENT_FINISHED":
      return { ...state, tournamentStatus: "finished" };
    default:
      return state;
  }
}

/**
 * Tournament-wide live state for `tournamentId`, independent of any table
 * connection. `enabled` gates the connection attempt entirely -- pass false
 * for a tournament the user has no entry in at all; the consumer rejects
 * with 4003 in that case anyway (see docs/consumers.md), so there's no
 * reason to attempt it.
 *
 * On a `table_reassigned` event naming this user with a real `to_table_id`,
 * this sends `{"type": "sync"}` on the SAME connection to get a fresh
 * `tournament_state` snapshot (the one payload shape that actually carries a
 * connectable `game_id` -- `table_reassigned` itself only carries
 * `to_table_id`, a TournamentTable pk, not something you can open a WS to
 * directly). Mirrors TournamentBot._reconnect_to_current_table's own
 * "re-resolve via the source of truth, don't trust the event's ids as
 * connectable" discipline on the backend.
 */
export function useTournamentChannel(tournamentId, { enabled = true } = {}) {
  const { user } = useUserContext();
  const myUserId = user?.id ?? null;
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const wsRef = useRef(null);
  const myUserIdRef = useRef(myUserId);

  useEffect(() => {
    myUserIdRef.current = myUserId;
  }, [myUserId]);

  useEffect(() => {
    if (!enabled || !tournamentId) return undefined;
    let ws = null;
    let stopped = false;
    let reconnectAttempt = 0;
    let reconnectTimer = null;

    // Same meaning as usePokerGame's set: deliberate rejections from the
    // consumer's connect() (bad id, unauthenticated, tournament not found, no
    // entry for this user) -- retrying against these just spins forever.
    const NON_RETRYABLE_CLOSE_CODES = new Set([4001, 4002, 4003, 4004]);
    const RECONNECT_BASE_DELAY_MS = 1000;
    const RECONNECT_MAX_DELAY_MS = 15000;
    const MAX_RECONNECT_ATTEMPTS = 8;

    const connect = async () => {
      const token = await ensureFreshAccessToken({ minTtlSeconds: 60 });
      if (!token || stopped) return;

      const base = String(config.websocketBaseUrl || "").replace(/\/+$/, "");
      ws = new WebSocket(`${base}/poker/mtt/${tournamentId}/?token=${token}`);
      wsRef.current = ws;
      dispatch({ type: "WS_STATUS", wsStatus: "connecting" });

      ws.onopen = () => {
        reconnectAttempt = 0;
        dispatch({ type: "WS_STATUS", wsStatus: "connected" });
      };
      ws.onclose = (event) => {
        if (stopped) return;
        if (NON_RETRYABLE_CLOSE_CODES.has(event?.code)) {
          dispatch({ type: "WS_STATUS", wsStatus: "rejected" });
          return;
        }
        dispatch({ type: "WS_STATUS", wsStatus: "disconnected" });
        if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) return;

        reconnectAttempt += 1;
        const delay = Math.min(
          RECONNECT_BASE_DELAY_MS * 2 ** (reconnectAttempt - 1),
          RECONNECT_MAX_DELAY_MS
        );
        reconnectTimer = window.setTimeout(connect, delay);
      };
      ws.onerror = () => dispatch({ type: "WS_STATUS", wsStatus: "disconnected" });
      ws.onmessage = (evt) => {
        let data;
        try {
          data = JSON.parse(evt.data);
        } catch {
          return;
        }

        if (data.type === "tournament_state") {
          dispatch({
            type: "SNAPSHOT",
            status: data.status,
            levelIndex: data.level_index,
            smallBlind: data.small_blind,
            bigBlind: data.big_blind,
            ante: data.ante,
            secondsRemaining: data.seconds_remaining,
            myStatus: data.my_status,
            myFinishPosition: data.my_finish_position,
            currentTableId: data.current_table_id,
            gameId: data.game_id,
          });
        } else if (data.type === "blind_level_changed") {
          dispatch({
            type: "BLIND_LEVEL_CHANGED",
            levelIndex: data.level_index,
            smallBlind: data.small_blind,
            bigBlind: data.big_blind,
            ante: data.ante,
            secondsRemaining: data.seconds_remaining,
          });
        } else if (data.type === "player_eliminated") {
          if (myUserIdRef.current != null && data.user_id === myUserIdRef.current) {
            dispatch({ type: "PLAYER_ELIMINATED_SELF", finishPosition: data.finish_position });
          }
        } else if (data.type === "table_reassigned") {
          if (myUserIdRef.current != null && data.user_id === myUserIdRef.current && data.to_table_id != null) {
            // Just learned we have a (new) table, but this event only carries
            // to_table_id (a TournamentTable pk), not a connectable game_id --
            // ask the same connection for a fresh snapshot rather than guess.
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "sync" }));
            }
          }
        } else if (data.type === "tournament_finished") {
          dispatch({ type: "TOURNAMENT_FINISHED" });
        }
      };
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
      wsRef.current = null;
    };
  }, [enabled, tournamentId]);

  return state;
}
